import { useTranslation } from '@multaqa/i18n'
import { toast } from '@multaqa/ui/components/ui/sonner'
import { useState } from 'react'
import type { CompanySettingResource, CompanySettingsUpdateInput } from './api'
import { useUpdateCompanySettings } from './api'
import { SettingsCard } from './company-settings-card'
import {
  SettingsNumberField,
  SettingsSelectField,
  SettingsToggleField,
  settingBooleanLabel,
} from './company-settings-fields'

const OVER_RECEIPT = ['block', 'allow'] as const
const EXPIRY = ['on', 'after'] as const
const ALLOCATION = ['fefo', 'fifo'] as const

export function CompanyPurchasingCard({
  groupId,
  companyId,
  settings,
}: {
  groupId: string
  companyId: string
  settings: CompanySettingResource
}) {
  const { t } = useTranslation()
  const update = useUpdateCompanySettings()
  const [editing, setEditing] = useState(false)
  const [values, setValues] = useState(() => fromSettings(settings))
  const [message, setMessage] = useState<string | null>(null)

  function reset() {
    setValues(fromSettings(settings))
    setMessage(null)
  }

  function save() {
    const terms = wholeNumber(values.terms)
    const warning = wholeNumber(values.warning)
    if (terms === null || warning === null) {
      setMessage(t('companies.settings.form.wholeNumber'))
      return
    }
    const input: CompanySettingsUpdateInput = {
      default_payment_terms_days: terms,
      allow_negative_stock: values.negative,
      default_expiry_convention: values.expiry,
      expiry_warning_days: warning,
      default_allocation_strategy: values.allocation,
      over_receipt_policy: values.overReceipt,
    }
    update.mutate(
      { groupId, companyId, input },
      {
        onSuccess: () => {
          setEditing(false)
          setMessage(null)
          toast.success(t('state.saved'))
        },
        onError: () => setMessage(t('companies.settings.form.saveFailed')),
      },
    )
  }

  return (
    <SettingsCard
      testId="company-settings-purchasing"
      title={t('companies.settings.groups.purchasing')}
      description={t('companies.settings.groups.purchasingDescription')}
      editing={editing}
      pending={update.isPending}
      error={Boolean(update.error)}
      message={message}
      onEdit={() => {
        reset()
        setEditing(true)
      }}
      onCancel={() => {
        reset()
        setEditing(false)
      }}
      onSave={save}
    >
      {editing ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <SettingsNumberField
            id="company-settings-payment-terms"
            label={t('companies.settings.fields.paymentTerms')}
            value={values.terms}
            onChange={(terms) => setValues((current) => ({ ...current, terms }))}
          />
          <SettingsSelectField
            id="company-settings-over-receipt"
            label={t('companies.settings.fields.overReceipt')}
            value={values.overReceipt}
            options={OVER_RECEIPT.map((value) => ({
              value,
              label: t(`companies.settings.values.overReceipt.${value}`),
            }))}
            onChange={(overReceipt) =>
              setValues((current) => ({
                ...current,
                overReceipt: isOverReceipt(overReceipt) ? overReceipt : current.overReceipt,
              }))
            }
          />
          <SettingsToggleField
            id="company-settings-negative-stock"
            label={t('companies.settings.fields.negativeStock')}
            checked={values.negative}
            onChange={(negative) => setValues((current) => ({ ...current, negative }))}
          />
          <SettingsSelectField
            id="company-settings-expiry"
            label={t('companies.settings.fields.expiryConvention')}
            value={values.expiry}
            options={EXPIRY.map((value) => ({
              value,
              label: t(`companies.settings.values.expiry.${value}`),
            }))}
            onChange={(expiry) =>
              setValues((current) => ({
                ...current,
                expiry: isExpiry(expiry) ? expiry : current.expiry,
              }))
            }
          />
          <SettingsNumberField
            id="company-settings-expiry-warning"
            label={t('companies.settings.fields.expiryWarningDays')}
            value={values.warning}
            onChange={(warning) => setValues((current) => ({ ...current, warning }))}
          />
          <SettingsSelectField
            id="company-settings-allocation"
            label={t('companies.settings.fields.allocationStrategy')}
            value={values.allocation}
            options={ALLOCATION.map((value) => ({
              value,
              label: t(`companies.settings.values.allocation.${value}`),
            }))}
            onChange={(allocation) =>
              setValues((current) => ({
                ...current,
                allocation: isAllocation(allocation) ? allocation : current.allocation,
              }))
            }
          />
        </div>
      ) : (
        <div className="grid gap-x-6 sm:grid-cols-2">
          <Value
            label={t('companies.settings.fields.paymentTerms')}
            value={`${settings.default_payment_terms_days} ${t('companies.settings.values.days')}`}
          />
          <Value
            label={t('companies.settings.fields.negativeStock')}
            value={settingBooleanLabel(settings.allow_negative_stock, t)}
          />
          <Value
            label={t('companies.settings.fields.expiryConvention')}
            value={t(`companies.settings.values.expiry.${settings.default_expiry_convention}`)}
          />
          <Value
            label={t('companies.settings.fields.expiryWarningDays')}
            value={String(settings.expiry_warning_days)}
          />
          <Value
            label={t('companies.settings.fields.allocationStrategy')}
            value={t(
              `companies.settings.values.allocation.${settings.default_allocation_strategy}`,
            )}
          />
          <Value
            label={t('companies.settings.fields.overReceipt')}
            value={t(`companies.settings.values.overReceipt.${settings.over_receipt_policy}`)}
          />
        </div>
      )}
    </SettingsCard>
  )
}

type PurchasingValues = {
  terms: string
  negative: boolean
  expiry: (typeof EXPIRY)[number]
  warning: string
  allocation: (typeof ALLOCATION)[number]
  overReceipt: (typeof OVER_RECEIPT)[number]
}

function fromSettings(settings: CompanySettingResource): PurchasingValues {
  return {
    terms: String(settings.default_payment_terms_days),
    negative: settings.allow_negative_stock,
    expiry: isExpiry(settings.default_expiry_convention)
      ? settings.default_expiry_convention
      : 'after',
    warning: String(settings.expiry_warning_days),
    allocation: isAllocation(settings.default_allocation_strategy)
      ? settings.default_allocation_strategy
      : 'fefo',
    overReceipt: isOverReceipt(settings.over_receipt_policy)
      ? settings.over_receipt_policy
      : 'block',
  }
}

function wholeNumber(value: string): number | null {
  if (!/^\d+$/.test(value.trim())) return null
  return Number(value)
}

function isExpiry(value: string): value is (typeof EXPIRY)[number] {
  return (EXPIRY as readonly string[]).includes(value)
}
function isAllocation(value: string): value is (typeof ALLOCATION)[number] {
  return (ALLOCATION as readonly string[]).includes(value)
}
function isOverReceipt(value: string): value is (typeof OVER_RECEIPT)[number] {
  return (OVER_RECEIPT as readonly string[]).includes(value)
}

function Value({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-b border-border py-3">
      <div className="text-label text-muted-foreground font-mono uppercase">{label}</div>
      <div className="text-body font-mono">{value}</div>
    </div>
  )
}
